import { useMemo } from 'react';
import { View, Text, ScrollView, Pressable, Image } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { ARENA_EMPTY_MONKEY_SOURCE } from '../../../constants/assets';
import { SportStandingsTable } from '../SportStandingsTable/SportStandingsTable';
import {
  buildStandingsRows,
  standingsTableHeading,
} from '../../../src/competitions/helpers/tournamentStandingsRows.helper';
import type { WorldCupGroupStageTable } from '../../../src/worldcup/types/worldCupTournamentStandings.types';
import {
  TOURNAMENT_STANDINGS_EMPTY_TITLE,
  TOURNAMENT_STANDINGS_EMPTY_SUBTITLE,
  TOURNAMENT_STANDINGS_ERROR_TITLE,
  TOURNAMENT_STANDINGS_RETRY,
  TOURNAMENT_STANDINGS_TEST_IDS,
} from './TournamentStandings.constants';
import { getTournamentStandingsStyles } from './TournamentStandings.styles';

export interface TournamentStandingsViewProps {
  groups: readonly WorldCupGroupStageTable[];
  sport?: string | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/**
 * A competition's standings (specs/champions-league, Story 5).
 *
 * The server decides whether a table may be shown at all — `League.standings` has no season
 * scoping, so before a new season syncs it still holds last campaign's finished table. An
 * empty `groups` here therefore means "not yet", not "failed to load", and reads as such.
 *
 * Columns come from `SportStandingsTable`, which picks them from the sport, and clubs draw as
 * kits rather than crests.
 */
export function TournamentStandingsView({
  groups, sport, isLoading, isError, onRetry,
}: TournamentStandingsViewProps) {
  const theme = useTheme();
  const s = useMemo(() => getTournamentStandingsStyles(theme), [theme]);

  if (isLoading) {
    return (
      <View style={s.screen} testID={TOURNAMENT_STANDINGS_TEST_IDS.screen}>
        <View style={s.skeletonRow} />
        <View style={s.skeletonRow} />
        <View style={s.skeletonRow} />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={s.screen} testID={TOURNAMENT_STANDINGS_TEST_IDS.screen}>
        <View style={s.centerWrap} testID={TOURNAMENT_STANDINGS_TEST_IDS.error}>
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={s.emptyMonkey} resizeMode="contain" />
          <Text style={s.stateTitle}>{TOURNAMENT_STANDINGS_ERROR_TITLE}</Text>
          <Pressable
            style={s.retryButton}
            onPress={onRetry}
            testID={TOURNAMENT_STANDINGS_TEST_IDS.retry}
            accessibilityRole="button"
          >
            <Text style={s.retryLabel}>{TOURNAMENT_STANDINGS_RETRY}</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  if (groups.length === 0) {
    return (
      <View style={s.screen} testID={TOURNAMENT_STANDINGS_TEST_IDS.screen}>
        <View style={s.centerWrap} testID={TOURNAMENT_STANDINGS_TEST_IDS.empty}>
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={s.emptyMonkey} resizeMode="contain" />
          <Text style={s.stateTitle}>{TOURNAMENT_STANDINGS_EMPTY_TITLE}</Text>
          <Text style={s.stateSubtitle}>{TOURNAMENT_STANDINGS_EMPTY_SUBTITLE}</Text>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      style={s.screen}
      contentContainerStyle={s.listContent}
      showsVerticalScrollIndicator={false}
      testID={TOURNAMENT_STANDINGS_TEST_IDS.screen}
    >
      {groups.map((group, index) => {
        const heading = standingsTableHeading(group);
        return (
          <View key={group.groupLetter || group.groupName || index} style={s.tableBlock}>
            {heading != null && (
              <Text style={s.heading} testID={TOURNAMENT_STANDINGS_TEST_IDS.heading(index)}>
                {heading}
              </Text>
            )}
            <View testID={TOURNAMENT_STANDINGS_TEST_IDS.table(index)}>
              <SportStandingsTable rows={buildStandingsRows(group)} sport={sport} />
            </View>
          </View>
        );
      })}
    </ScrollView>
  );
}
